import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Row,
  Col,
  Tag,
  Tooltip,
  Dropdown,
} from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import { DownOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  customerApi,
  type CustomerDTO,
  type CustomerAddressDTO,
  type KccAccountCredentialDTO,
  type CreateKccAccountRequest,
} from '../api/kfc';
import CustomerImportModal from '../components/CustomerImportModal';
import KccAccountCredentialModal from '../components/KccAccountCredentialModal';
import { useAuth } from '../context/AuthContext';
import { canViewOperationLogs } from '../utils/permissions';
import { clientTablePagination } from '../utils/tablePagination';

type ApiError = { response?: { data?: { message?: string } } };

const CustomerManage: React.FC = () => {
  const { session } = useAuth();
  const isAdmin = canViewOperationLogs(session.identity?.traits);
  const [list, setList] = useState<CustomerDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingKccOpened, setEditingKccOpened] = useState(false);
  const [kccBusyId, setKccBusyId] = useState<string | null>(null);
  const [credential, setCredential] = useState<KccAccountCredentialDTO | null>(null);
  const [kccCreateTarget, setKccCreateTarget] = useState<{ id: string; email: string } | null>(null);
  const [form] = Form.useForm();
  const [kccForm] = Form.useForm<CreateKccAccountRequest>();

  const load = () => {
    setLoading(true);
    customerApi
      .list()
      .then((res) => setList(res.data))
      .catch(() => {
        setList([]);
        message.warning('无法加载客户列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setEditingKccOpened(false);
    form.resetFields();
    form.setFieldsValue({ addresses: [{}] });
    setModalOpen(true);
  };

  const openEdit = (r: CustomerDTO) => {
    if (!r.id) return;
    setEditingId(r.id);
    customerApi
      .get(r.id)
      .then((res) => {
        const full = res.data;
        setEditingKccOpened(!!full.kccAccount?.opened);
        form.setFieldsValue({
          ...full,
          addresses: full.addresses?.length ? full.addresses : [{}],
        });
        setModalOpen(true);
      })
      .catch(() => message.error('加载客户详情失败'));
  };

  const onFinish = (values: Partial<CustomerDTO> & { addresses?: CustomerAddressDTO[] }) => {
    const { addresses, ...rest } = values;
    const payload = {
      ...rest,
      addresses:
        addresses?.filter(
          (a) =>
            a.label ||
            a.contactName ||
            a.companyName ||
            a.email ||
            a.address ||
            a.phone ||
            a.remark ||
            a.postalCode
        ) ?? [],
    };
    const submit = editingId
      ? customerApi.update(editingId, payload)
      : customerApi.create(payload);
    submit
      .then(() => {
        message.success(editingId ? '更新成功' : '创建成功');
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const doDelete = (id: string) => {
    customerApi
      .delete(id)
      .then(() => {
        message.success('已删除');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  const runKccAction = (
    id: string,
    action: (id: string) => Promise<{ data: KccAccountCredentialDTO }>,
    successText: string,
    failText: string,
    reload: boolean
  ): Promise<boolean> => {
    setKccBusyId(id);
    return action(id)
      .then((res) => {
        message.success(successText);
        setCredential(res.data);
        if (reload) load();
        return true;
      })
      .catch((e: ApiError) => {
        message.error(e.response?.data?.message || failText);
        return false;
      })
      .finally(() => setKccBusyId(null));
  };

  const submitCreateKcc = async (values: CreateKccAccountRequest) => {
    if (!kccCreateTarget) return;
    const body = { firstName: values.firstName.trim(), lastName: values.lastName.trim() };
    const ok = await runKccAction(
      kccCreateTarget.id,
      (id) => customerApi.createKccAccount(id, body),
      'KCC 账号已创建',
      '创建 KCC 账号失败',
      true
    );
    if (ok) setKccCreateTarget(null);
  };

  const viewKccPassword = (id: string) =>
    runKccAction(id, customerApi.getKccPassword, '已获取密码', '查看密码失败', false);

  const confirmResetKcc = (id: string) =>
    Modal.confirm({
      title: '重置 KCC 密码',
      content: '重置后旧密码失效，已登录的会话将被下线，确定？',
      okText: '重置',
      okButtonProps: { danger: true },
      onOk: () => runKccAction(id, customerApi.resetKccPassword, '密码已重置', '重置密码失败', true),
    });

  const renderKccColumn = (r: CustomerDTO) => {
    const kcc = r.kccAccount;
    if (!kcc?.opened) return <span style={{ color: '#999' }}>未开通</span>;
    return (
      <Space size={4} wrap>
        <Tag color="green">{kcc.loginEmail}</Tag>
        {kcc.status === 'DISABLED' && <Tag color="default">已停用</Tag>}
        {!kcc.passwordKnown && (
          <Tooltip title="密码未知，请重置">
            <Tag color="orange">密码未知</Tag>
          </Tooltip>
        )}
      </Space>
    );
  };

  const renderKccAction = (r: CustomerDTO) => {
    if (!r.id || !isAdmin) return null;
    const id = r.id;
    const busy = kccBusyId === id;
    if (!r.kccAccount?.opened) {
      const email = r.email?.trim();
      if (!email) {
        return (
          <Tooltip title="请先填写客户邮箱">
            <Button type="link" size="small" disabled>
              创建kcc账号
            </Button>
          </Tooltip>
        );
      }
      return (
        <Button type="link" size="small" loading={busy} onClick={() => setKccCreateTarget({ id, email })}>
          创建kcc账号
        </Button>
      );
    }
    return (
      <Dropdown
        disabled={busy}
        menu={{
          items: [
            { key: 'view', label: '查看密码', disabled: !r.kccAccount.passwordKnown },
            { key: 'reset', label: '重置密码', danger: true },
          ],
          onClick: ({ key }) => (key === 'view' ? viewKccPassword(id) : confirmResetKcc(id)),
        }}
      >
        <Button type="link" size="small" loading={busy}>
          kcc账号 <DownOutlined />
        </Button>
      </Dropdown>
    );
  };

  const downloadTemplate = () => {
    customerApi
      .downloadTemplate()
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'kfy-customer-list.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => message.error('模板下载失败'));
  };

  return (
    <>
      <Card
        title="客户列表"
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              下载模板
            </Button>
            <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
              导入客户
            </Button>
            <Button type="primary" onClick={openAdd}>
              新增客户
            </Button>
          </Space>
        }
      >
        <AdaptiveTable
          loading={loading}
          dataSource={list}
          rowKey="id"
          pagination={clientTablePagination()}
          columns={[
            { title: '客户标识', dataIndex: 'customerCode', width: 120 },
            { title: '客户名称', dataIndex: 'name', width: 140 },
            { title: '域名', dataIndex: 'domain', width: 140, ellipsis: true },
            { title: '邮箱', dataIndex: 'email', width: 160, ellipsis: true },
            { title: '电话', dataIndex: 'phone', width: 120 },
            { title: 'KCC账号', key: 'kccAccount', width: 200, render: (_, r) => renderKccColumn(r) },
            {
              title: '操作',
              width: 260,
              render: (_, r) => (
                <Space size={0}>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title={
                      r.kccAccount?.opened
                        ? '确定删除该客户吗？将同时删除其 KCC 登录账号'
                        : '确定删除该客户吗？'
                    }
                    onConfirm={() => r.id != null && doDelete(r.id)}
                  >
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                  {renderKccAction(r)}
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑客户' : '新增客户'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="customerCode" label="客户标识" rules={[{ required: true, message: '请输入客户标识' }]}>
            <Input placeholder="唯一标识" disabled={!!editingId} />
          </Form.Item>
          <Form.Item name="domain" label="域名">
            <Input placeholder="为空则使用 {客户标识}.com" />
          </Form.Item>
          <Form.Item name="name" label="客户名称" rules={[{ required: true, message: '请输入客户名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="客户邮箱"
            extra={editingKccOpened ? '已开通 KCC 账号，邮箱暂不支持修改' : undefined}
          >
            <Input type="email" readOnly={editingKccOpened} />
          </Form.Item>
          <Form.Item name="phone" label="客户电话">
            <Input />
          </Form.Item>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>客户地址（可选）</div>
          <Form.List name="addresses">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <div key={key} style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item
                          {...rest}
                          name={[name, 'label']}
                          label="地址标识"
                          rules={[{ required: true, message: '请输入地址标识' }]}
                        >
                          <Input placeholder="地址标识" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'remark']} label="备注">
                          <Input placeholder="备注" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'contactName']} label="联系人">
                          <Input placeholder="联系人" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'companyName']} label="公司名称">
                          <Input placeholder="公司名称" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'email']} label="邮箱">
                          <Input type="email" placeholder="邮箱" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'phone']} label="电话">
                          <Input placeholder="电话" />
                        </Form.Item>
                      </Col>
                      <Col span={16}>
                        <Form.Item {...rest} name={[name, 'address']} label="地址">
                          <Input placeholder="地址" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item {...rest} name={[name, 'postalCode']} label="邮编">
                          <Input placeholder="邮编" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Button type="text" danger onClick={() => remove(name)}>
                      删除此地址
                    </Button>
                  </div>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block>
                    + 添加地址
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <CustomerImportModal open={importOpen} onClose={() => setImportOpen(false)} onSuccess={load} />
      <Modal
        title="创建 KCC 账号"
        open={!!kccCreateTarget}
        okText="创建"
        confirmLoading={!!kccCreateTarget && kccBusyId === kccCreateTarget.id}
        onOk={() => kccForm.submit()}
        onCancel={() => setKccCreateTarget(null)}
        destroyOnClose
      >
        <Form form={kccForm} layout="vertical" preserve={false} onFinish={submitCreateKcc}>
          <Form.Item label="登录邮箱">
            <Input value={kccCreateTarget?.email} disabled />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First name"
                rules={[
                  { required: true, whitespace: true, message: '请填写 First name' },
                  { max: 64, message: '不能超过 64 个字符' },
                ]}
              >
                <Input placeholder="First name" autoFocus />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last name"
                rules={[
                  { required: true, whitespace: true, message: '请填写 Last name' },
                  { max: 64, message: '不能超过 64 个字符' },
                ]}
              >
                <Input placeholder="Last name" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <KccAccountCredentialModal open={!!credential} credential={credential} onClose={() => setCredential(null)} />
    </>
  );
};

export default CustomerManage;
